import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, StatusBadge } from "@fawkes/ui";
import { ArrowLeft, Lock, Mail, MessageSquare, MessagesSquare, Search, Send } from "lucide-react";

import { formatDateTime } from "../i18n/format";
import { foutBody } from "./klantverzoeken/types";
import "./OpvangModules.css";
import "./PleeggezinOverzicht.css";
import "./Klantverzoeken.css";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

/**
 * Klantberichten: wat klanten via het portaal aan het pension schrijven (ontwerp hoofdstuk 13.1), met de
 * draden die nog een antwoord nodig hebben bovenaan. Een antwoord gaat als portaalbericht; de klant krijgt een
 * e-mail dat er een bericht is, zonder de inhoud. Een bericht verandert nooit iets aan reservering of klant.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

export interface BerichtRegel {
  id: string;
  categorie: string;
  onderwerp: string;
  zichtbaarheid: "HOUSEHOLD" | "RECIPIENT_ONLY";
  huishouden: string | null;
  klantId: number | null;
  laatsteOp: string;
  openSinds: string | null;
  laatsteAfzender: string | null;
  voorproefje: string | null;
  laatsteRichting: "IN" | "UIT" | null;
  aantal: number;
}

interface Bericht {
  id: string;
  kanaal: "EMAIL" | "PORTAAL" | "SYSTEEM";
  richting: "IN" | "UIT";
  zichtbaarheid: string;
  afzender: string | null;
  onderwerp: string | null;
  inhoud: string | null;
  verzendstatus: "NVT" | "WACHT" | "VERSTUURD" | "MISLUKT";
  medewerker: string | null;
  op: string;
}

interface Draad {
  id: string;
  categorie: string;
  onderwerp: string;
  zichtbaarheid: "HOUSEHOLD" | "RECIPIENT_ONLY";
  huishouden: string | null;
  klantId: number | null;
  verzoekId: string | null;
  open: boolean;
  berichten: Bericht[];
}

const CATEGORIEEN = ["RESERVERING", "VERZOEK", "FACTUUR", "ALGEMEEN", "ACCOUNT"] as const;

function initialen(naam: string): string {
  const w = naam.replace(/^(fam\.?|familie|mevr\.?|dhr\.?)\s+/i, "").split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? "?") + (w.length > 1 ? w[w.length - 1][0] : "")).toUpperCase();
}

/**
 * Het overzicht in de opbouw van de asieloverzichten (Maarten, 2026-10-05: "alle overzichten"): kop met tegels,
 * tabs per onderwerp met een rood rondje voor wat op antwoord wacht, een filterkaart, en kaarten of een lijst.
 */
export function Klantberichten({ onOpen }: { onOpen: (id: string) => void }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const [categorie, setCategorie] = useState("ALLE");
  const [alleenOpen, setAlleenOpen] = useState(true);
  const [persoonlijk, setPersoonlijk] = useState(false);
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useState<"kaart" | "lijst">("kaart");
  const [sorteer, setSorteer] = useState("nieuwste");
  // Alles in één keer; "antwoord nodig" is een filter op openSinds, zodat de tellers altijd kloppen.
  const { data, isLoading, isError } = useCustom<BerichtRegel[]>({ url: "klantberichten?open=false", method: "get" });
  const alle = Array.isArray(data?.data) ? data.data : [];
  const inCategorie = alle.filter((r) => categorie === "ALLE" || r.categorie === categorie);
  const z = zoek.trim().toLowerCase();
  const zichtbaar = inCategorie
    .filter((r) => !alleenOpen || r.openSinds)
    .filter((r) => !persoonlijk || r.zichtbaarheid === "RECIPIENT_ONLY")
    .filter((r) => !z || [r.onderwerp, r.huishouden, r.voorproefje, r.laatsteAfzender].some((v) => (v ?? "").toLowerCase().includes(z)))
    .sort((a, b) => {
      if (sorteer === "oudste") return a.laatsteOp.localeCompare(b.laatsteOp);
      if (sorteer === "klant") return (a.huishouden ?? "").localeCompare(b.huishouden ?? "", "nl");
      return b.laatsteOp.localeCompare(a.laatsteOp);
    });
  const nodig = (lijst: BerichtRegel[]) => lijst.filter((r) => r.openSinds).length;
  const wis = () => {
    setZoek("");
    setAlleenOpen(false);
    setPersoonlijk(false);
  };
  const status = (r: BerichtRegel) =>
    r.openSinds ? (
      <StatusBadge tone="warning">{t("klantberichten.antwoordNodig")}</StatusBadge>
    ) : (
      <StatusBadge tone="neutral">{t("klantberichten.beantwoord")}</StatusBadge>
    );

  return (
    <div className="fawkes-pgo kvo">
      <header className="pg__head">
        <div className="pg__titel">
          <div className="pg__avatar"><MessagesSquare size={22} aria-hidden="true" /></div>
          <div>
            <span className="pg__overline">{t("klantberichten.overline")}</span>
            <h1>{t("klantberichten.titel")}</h1>
            <span className="pg__vestiging">{t("klantberichten.subtitel")}</span>
          </div>
        </div>
        <div className="soorten">
          <button type="button" className={"soortKaart kvo__fase kvo__fase--mislukt" + (alleenOpen ? " actief" : "")}
            onClick={() => setAlleenOpen(true)}>
            <span className="soortIcon"><Mail size={20} aria-hidden="true" /></span>
            <span className="soortBody">
              <span className="soortAantal">{nodig(inCategorie)}</span>
              <span className="soortNaam">{t("klantberichten.antwoordNodig")}</span>
            </span>
          </button>
          <button type="button" className="soortKaart kvo__fase kvo__fase--verwerkt" onClick={() => setAlleenOpen(false)}>
            <span className="soortIcon"><Send size={20} aria-hidden="true" /></span>
            <span className="soortBody">
              <span className="soortAantal">{inCategorie.length - nodig(inCategorie)}</span>
              <span className="soortNaam">{t("klantberichten.beantwoord")}</span>
            </span>
          </button>
          <button type="button" className={"soortKaart totaal" + (!alleenOpen ? " actief" : "")} onClick={() => setAlleenOpen(false)}>
            <span className="soortIcon"><MessagesSquare size={20} aria-hidden="true" /></span>
            <span className="soortBody">
              <span className="soortAantal">{inCategorie.length}</span>
              <span className="soortNaam">{t("klantverzoeken.fase.totaal")}</span>
            </span>
          </button>
        </div>
      </header>

      <nav className="tabbar" role="tablist">
        {["ALLE", ...CATEGORIEEN].map((c) => {
          const n = nodig(alle.filter((r) => c === "ALLE" || r.categorie === c));
          return (
            <button key={c} type="button" role="tab" aria-selected={categorie === c}
              className={"tabbar__tab" + (categorie === c ? " is-active" : "")} onClick={() => setCategorie(c)}>
              {c === "ALLE" ? t("klantberichten.filter.alle") : t(`klantberichten.categorie.${c}`)}
              {n > 0 && <span className="tabbar__badge kvo__tabbadge">{n}</span>}
            </button>
          );
        })}
      </nav>

      <div className="pg__body">
        <section className="filters">
          <div className="pgFilterRij">
            <div className="zoek">
              <span className="zoekIcon"><Search size={16} aria-hidden="true" /></span>
              <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder={t("klantberichten.zoek")}
                aria-label={t("klantberichten.zoek")} />
            </div>
            <div className="pgToggles">
              <button type="button" className={"toggleChip" + (alleenOpen ? " aan" : "")} onClick={() => setAlleenOpen((v) => !v)}>
                <span className="toggleDot" /> {t("klantberichten.filter.open")}
              </button>
              <button type="button" className={"toggleChip" + (persoonlijk ? " aan" : "")} onClick={() => setPersoonlijk((v) => !v)}>
                <span className="toggleDot" /> {t("klantberichten.persoonlijk")}
              </button>
            </div>
            <button type="button" className="wisBtn" onClick={wis}><span className="wisIcon">↻</span> {t("pgOverzicht.wisFilters")}</button>
          </div>
        </section>

        <div className="metaBar">
          <span className="resultaten">
            <MessageSquare size={16} aria-hidden="true" />
            {t("klantberichten.resultaten", { args: [zichtbaar.length] })}
          </span>
          <div className="rechts">
            <div className="weergaveTabs">
              <button type="button" className={weergave === "kaart" ? "actief" : ""} onClick={() => setWeergave("kaart")}
                title={t("pgOverzicht.weergave.kaarten")}>▦</button>
              <button type="button" className={weergave === "lijst" ? "actief" : ""} onClick={() => setWeergave("lijst")}
                title={t("pgOverzicht.weergave.lijst")}>☰</button>
            </div>
            <label className="metaSort"><span>{t("pgOverzicht.sorterenOp")}</span>
              <select value={sorteer} onChange={(e) => setSorteer(e.target.value)}>
                <option value="nieuwste">{t("klantverzoeken.sorteer.nieuwste")}</option>
                <option value="oudste">{t("klantverzoeken.sorteer.oudste")}</option>
                <option value="klant">{t("klantverzoeken.sorteer.klant")}</option>
              </select>
            </label>
          </div>
        </div>

        {isError && <p className="fawkes-error" role="alert">{t("klantberichten.fout.laden")}</p>}
        {isLoading ? <p className="leeg">{t("common.loading")}</p> : zichtbaar.length === 0 ? (
          <p className="leeg">{t(alleenOpen ? "klantberichten.leegOpen" : "klantberichten.leeg")}</p>
        ) : weergave === "lijst" ? (
          <section className="lijst">
            <IndeelbareTabel
              tabelKey="klantberichten"
              columns={[
                {
                  key: "klant", header: t("klantberichten.kolom.klant"),
                  // Het streepje links is de leesstand: rood zolang hij openstaat, groen als hij af is.
                  cell: (r) => (
                    <span className="lnaam"
                      style={{ boxShadow: `inset 4px 0 0 ${r.openSinds ? "#dc2626" : "#16a34a"}` }}>
                      <b>{r.huishouden ?? "—"}</b>
                    </span>
                  ),
                },
                { key: "status", header: t("klantberichten.kolom.status"), cell: (r) => status(r) },
                {
                  key: "onderwerp", header: t("klantberichten.kolom.onderwerp"),
                  cell: (r) => (
                    <span className="kvo__aanvraag">
                      <b>{r.onderwerp}</b>
                      {r.voorproefje && <span className="kvo__voorproef">{r.voorproefje}</span>}
                    </span>
                  ),
                },
                {
                  key: "categorie", header: t("klantberichten.kolom.categorie"),
                  cell: (r) => t(`klantberichten.categorie.${r.categorie}`),
                },
                {
                  key: "laatste", header: t("klantberichten.kolom.laatste"),
                  cell: (r) => formatDateTime(r.laatsteOp, locale),
                },
              ]}
              rows={zichtbaar}
              getRowKey={(r) => String(r.id)}
              onRowClick={(r) => onOpen(r.id)}
              empty=""
            />
          </section>
        ) : (
          <div className="relaties">
            {zichtbaar.map((r) => {
              const kleur = r.openSinds ? "#dc2626" : "#16a34a";
              const naam = r.huishouden ?? "—";
              return (
                <button key={r.id} type="button" className="relatie kvo__kaart" style={{ borderLeft: `5px solid ${kleur}` }}
                  onClick={() => onOpen(r.id)}>
                  <div className="relatie__hoofd">
                    <div className="relatie__avatar" style={{ background: kleur + "22", color: kleur }}>{initialen(naam)}</div>
                    <div className="relatie__id">
                      <span className="relatie__naam">{naam}</span>
                      <span className="relatie__rol">{t(`klantberichten.categorie.${r.categorie}`)}</span>
                    </div>
                    {status(r)}
                  </div>
                  <div className="relatie__contact">
                    <div className="relatie__regel kvo__wat"><MessageSquare size={15} className="mi" aria-hidden="true" /><span>{r.onderwerp}</span></div>
                    {r.voorproefje && <p className="kvo__voorproef">{r.voorproefje}</p>}
                    <div className="relatie__regel">
                      <Mail size={15} className="mi" aria-hidden="true" />
                      <span>{formatDateTime(r.laatsteOp, locale)}{r.laatsteAfzender ? ` · ${r.laatsteAfzender}` : ""}</span>
                    </div>
                  </div>
                  <div className="kvo__voet">
                    <span className="chip-tag chip-tag--groen">{t("klantberichten.aantal", { args: [r.aantal] })}</span>
                    {r.zichtbaarheid === "RECIPIENT_ONLY" && (
                      <span className="kv__nieuw"><Lock size={13} aria-hidden="true" /> {t("klantberichten.persoonlijk")}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function KlantberichtDetail({ id, onBack, onOpenKlant }: {
  id: string;
  onBack: () => void;
  onOpenKlant: (klantId: number) => void;
}) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, isError, refetch } = useCustom<Draad>({ url: `klantberichten/${id}`, method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [tekst, setTekst] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  const d = data?.data;

  async function antwoord() {
    setFout(null);
    try {
      await mutateAsync({ url: `klantberichten/${id}/antwoord`, method: "post", values: { tekst },
        successNotification: false, errorNotification: false });
      setTekst("");
      await refetch();
    } catch (err) {
      setFout(foutBody(err).melding ?? t("klantverzoeken.fout.actie"));
    }
  }

  if (isLoading) {
    return <p className="kv__laden">{t("common.loading")}</p>;
  }
  if (isError || !d) {
    return <p className="fawkes-error" role="alert">{t("klantberichten.fout.laden")}</p>;
  }

  return (
    <div className="kv kvd">
      <button type="button" className="kvd__terug" onClick={onBack}>
        <ArrowLeft size={16} aria-hidden="true" /> {t("klantberichten.titel")}
      </button>
      <header className="kvd__kop">
        <div>
          <h1>{d.onderwerp}</h1>
          <p className="kvd__sub">
            {d.klantId != null ? (
              <button type="button" className="kvd__klantlink" onClick={() => onOpenKlant(d.klantId!)}>{d.huishouden}</button>
            ) : d.huishouden}
            {" · "}{t(`klantberichten.categorie.${d.categorie}`)}
            {" · "}{t(d.zichtbaarheid === "RECIPIENT_ONLY" ? "klantberichten.persoonlijk" : "klantberichten.huishouden")}
          </p>
        </div>
        {d.open && <StatusBadge tone="warning">{t("klantberichten.antwoordNodig")}</StatusBadge>}
      </header>

      <ol className="kbb__gesprek">
        {d.berichten.map((b) => (
          <li key={b.id} className={b.richting === "IN" ? "kbb__klant" : "kbb__pension"}>
            <div className="kvd__stil">
              {b.kanaal === "EMAIL" ? <Mail size={13} aria-hidden="true" /> : <MessageSquare size={13} aria-hidden="true" />}{" "}
              <strong>{b.richting === "IN" ? b.afzender : b.medewerker ?? b.afzender}</strong>
              {" · "}{formatDateTime(b.op, locale)}
              {" · "}{t(`klantberichten.kanaal.${b.kanaal}`)}
              {b.verzendstatus === "MISLUKT" && <> · <span className="kbb__mislukt">{t("klantberichten.nietBezorgd")}</span></>}
              {b.verzendstatus === "WACHT" && <> · {t("klantberichten.wacht")}</>}
            </div>
            {b.onderwerp && b.onderwerp !== d.onderwerp && <strong>{b.onderwerp}</strong>}
            <p>{b.inhoud}</p>
          </li>
        ))}
      </ol>

      <form className="kvd__form kvd__blok" onSubmit={(e) => { e.preventDefault(); void antwoord(); }}>
        <label>
          {t("klantberichten.antwoord")}
          <textarea rows={4} maxLength={5000} value={tekst} onChange={(e) => setTekst(e.target.value)} />
        </label>
        <p className="kvd__stil">{t("klantberichten.antwoordUitleg")}</p>
        {fout && <p className="fawkes-error" role="alert">{fout}</p>}
        <div className="kvd__acties">
          <Button variant="primary" type="submit" disabled={bezig || !tekst.trim()}>
            <Send size={16} aria-hidden="true" /> {t("klantberichten.versturen")}
          </Button>
        </div>
      </form>
    </div>
  );
}
